import styled from '@emotion/styled'

export const AUTH_MOBILE_BREAKPOINT = '768px'

export const AUTH_COLORS = {
  blue: '#122a41',
  orange: '#ff6422',
  disabledBlue: 'rgba(18, 42, 65, 0.6)',
  blueMuted: 'rgba(18, 42, 65, 0.7)',
  blueFocusShadow: 'rgba(18, 42, 65, 0.08)',
}

export const maskCpf = (value: string) =>
  value
    .replace(/\D/g, '')
    .slice(0, 11)
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2')

export const AuthFormBox = styled.section`
  width: 100%;
  max-width: 580px;

  form {
    margin-top: 24px;
  }

  @media (max-width: ${AUTH_MOBILE_BREAKPOINT}) {
    max-width: none;
  }
`

export const AuthTitle = styled.h1`
  color: ${AUTH_COLORS.blue};
  font-size: 38px;
  font-weight: 400;
  line-height: 1;
  margin: 0;

  strong {
    font-weight: 600;
  }

  @media (max-width: ${AUTH_MOBILE_BREAKPOINT}) {
    font-size: 30px;
  }
`

export const AuthSubtitle = styled.p`
  color: ${AUTH_COLORS.blue};
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  margin: 14px 0 0;

  strong {
    font-weight: 500;
  }

  @media (max-width: ${AUTH_MOBILE_BREAKPOINT}) {
    margin-top: 16px;
  }
`

export const AuthField = styled.div<{ hasError: boolean }>(
  (props) => `
  width: 100%;
  margin-top: 24px;

  & + & {
    margin-top: 16px;
  }

  label {
    display: block;
    color: ${AUTH_COLORS.blueMuted};
    font-size: 12px;
    font-weight: 500;
    line-height: 16px;
  }

  label span {
    font-weight: 400;
  }

  label + input,
  label + div {
    margin-top: 8px;
  }

  input {
    width: 100%;
    height: 48px;
    border: 1px solid ${props.hasError ? 'rgba(251, 44, 54, 1)' : 'rgba(209, 215, 224, 1)'};
    border-radius: 8px;
    background: ${props.hasError ? 'rgba(254, 242, 242, 1)' : '#ffffff'};
    color: ${AUTH_COLORS.blue};
    font-family: Rubik, sans-serif;
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    outline: none;
    padding: 0 12px;
    transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  }

  input::placeholder {
    color: #cbd3df;
  }

  input:not(:placeholder-shown),
  input:-webkit-autofill,
  input:focus {
    border-color: ${props.hasError ? 'rgba(251, 44, 54, 1)' : AUTH_COLORS.orange};
    background: ${props.hasError ? 'rgba(254, 242, 242, 1)' : 'rgba(249, 236, 231, 1)'};
    box-shadow: 0 0 0 2px ${props.hasError ? 'rgba(255, 45, 45, 0.08)' : AUTH_COLORS.blueFocusShadow};
  }

  input + button {
    color: ${props.hasError ? 'rgba(231, 0, 11, 1)' : 'rgba(90, 106, 122, 1)'};
  }

  input:not(:placeholder-shown) + button,
  input:-webkit-autofill + button,
  input:focus + button {
    color: ${props.hasError ? 'rgba(231, 0, 11, 1)' : AUTH_COLORS.orange};
  }

  .field-error {
    margin: 6px 0 0;
    color: rgba(231, 0, 11, 1);
    font-size: 12px;
    line-height: 16px;
    font-weight: 500;
  }

  @media (max-width: ${AUTH_MOBILE_BREAKPOINT}) {
    label {
      font-size: 12px;
      line-height: 16px;
    }

    label + input,
    label + div {
      margin-top: 8px;
    }

    input {
      height: 48px;
      font-size: 16px;
      line-height: 24px;
      padding: 12px;
    }
  }
`
)

export const AuthPrimaryButton = styled.button`
  width: 100%;
  height: 48px;
  margin-top: 24px;
  border: 1px solid ${AUTH_COLORS.blue};
  border-radius: 6px;
  background: ${AUTH_COLORS.blue};
  box-shadow: 0px 1px 4px rgba(25, 33, 61, 0.08);
  color: #ffffff;
  font-family: Rubik, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  transition:
    background 0.2s ease,
    border-color 0.2s ease,
    opacity 0.2s ease;

  ion-icon {
    width: 14px;
    height: 14px;
  }

  :hover:not(:disabled) {
    background: ${AUTH_COLORS.orange};
    border-color: ${AUTH_COLORS.orange};
  }

  :disabled {
    background: ${AUTH_COLORS.disabledBlue};
    border-color: ${AUTH_COLORS.disabledBlue};
    cursor: default;
  }
`

export const AuthPasswordWrap = styled.div`
  position: relative;

  input {
    padding-right: 48px;
  }

  @media (max-width: ${AUTH_MOBILE_BREAKPOINT}) {
    input {
      padding-right: 64px;
    }
  }
`

export const AuthPasswordToggle = styled.button`
  position: absolute;
  right: 11px;
  top: 50%;
  transform: translateY(-50%);
  width: 28px;
  height: 28px;
  border: 0;
  padding: 0;
  background: transparent;
  color: rgba(90, 106, 122, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: color 0.2s ease;

  :hover {
    color: ${AUTH_COLORS.orange};
  }

  ion-icon {
    width: 18px;
    height: 18px;
  }

  @media (max-width: ${AUTH_MOBILE_BREAKPOINT}) {
    right: 18px;
    width: 36px;
    height: 36px;

    ion-icon {
      width: 22px;
      height: 22px;
    }
  }
`
